import React, { useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Badge,
} from "reactstrap";

/**
 * ScheduledReportDeleteModal – Zamanlanmış rapor silme onayı
 *
 * Props
 * ─────────────────────────────────────────────────────────
 * @param {boolean}  isOpen      – Modal açık mı?
 * @param {Function} toggle      – Modal'ı kapat/aç
 * @param {Object}   report      – Silinecek rapor bilgileri
 * @param {Function} onConfirm   – Silme onaylandığında çalışır
 */
const ScheduledReportDeleteModal = ({ isOpen, toggle, report, onConfirm }) => {
    const [deleting, setDeleting] = useState(false);

    if (!report) return null;

    /* ─────────────────────────────────────────────────────────
     * Yardımcı Fonksiyonlar
     * ───────────────────────────────────────────────────────── */
    const translateScheduleType = (t) => {
        const map = {
            DAILY: "Günlük",
            WEEKLY: "Haftalık",
            MONTHLY: "Aylık",
            QUARTERLY: "3 Aylık"
        };
        return map[t] || t || "-";
    };

    const translateAccountType = (t) => {
        const map = {
            SOLE_PROPRIETORSHIP: "Şahıs Şirketi",
            JOINT_STOCK_COMPANY: "Anonim Şirket",
            LIMITED_COMPANY: "Limited Şirket",
            COOPERATIVE: "Kooperatif",
            GENERAL_PARTNERSHIP: "Kolektif Şirket",
            COMMANDITE_COMPANY: "Komandit Şirket",
            INDIVIDUAL: "Bireysel",
            CORPORATE: "Kurumsal",
        };
        return map[t] || t || "-";
    };

    const handleConfirm = async () => {
        if (!onConfirm || !report.id) return;

        setDeleting(true);
        try {
            await onConfirm(report.id);
            // Başarılı olursa, modalı kapat
            toggle(true);
        } catch (error) {
            console.error("Delete error:", error);
            // Hata mesajı gösterebilirsiniz
        } finally {
            setDeleting(false);
        }
    };

    /* ─────────────────────────────────────────────────────────
     * JSX
     * ───────────────────────────────────────────────────────── */
    return (
        <Modal isOpen={isOpen} toggle={toggle} size="md" centered>
            <ModalHeader toggle={toggle} className="border-bottom">
                <div className="d-flex align-items-center gap-2 text-danger">
                    <i className="bx bx-trash fs-4"></i>
                    Zamanlanmış Rapor Sil
                </div>
            </ModalHeader>

            <ModalBody className="py-4">

                <div className="text-center mb-4">
                    <div className="mb-3">
                        <i className="bx bx-file-blank display-4 text-muted"></i>
                    </div>
                    <p className="text-muted mb-2">Aşağıdaki zamanlanmış raporu silmek istediğinizden emin misiniz?</p>
                </div>

                {/* Rapor Bilgileri Özeti */}
                <div className="border rounded p-3 bg-light">
                    <div className="row">
                        <div className="col-12 mb-2">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h6 className="mb-1 text-dark">Alıcı E-posta: {report.recipientEmail || "-"}</h6>
                                    <div className="text-muted small">
                                        Zamanlama Türü: <strong>{translateScheduleType(report.scheduleType)}</strong>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <Badge color="info">
                                        {translateScheduleType(report.scheduleType)}
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        {report.accountName && (
                            <div className="col-12 mt-2 pt-2 border-top">
                                <div className="text-muted small">
                                    <i className="bx bx-credit-card-front me-1"></i>
                                    Hesap: {report.accountName}
                                </div>
                            </div>
                        )}

                        {report.accountTypes && report.accountTypes.length > 0 && (
                            <div className="col-12 mt-2 pt-2 border-top">
                                <div className="text-dark small mb-1">Cari Türleri:</div>
                                <div className="d-flex flex-wrap gap-1">
                                    {report.accountTypes.map((type, index) => (
                                        <Badge
                                            key={index}
                                            color="light"
                                            className="small text-dark border"
                                        >
                                            {translateAccountType(type)}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </ModalBody>

            <ModalFooter className="border-top">
                <div className="d-flex justify-content-end gap-2 w-100">
                    <Button
                        color="secondary"
                        onClick={toggle}
                        disabled={deleting}
                        className="rounded-pill px-4"
                    >
                        İptal
                    </Button>
                    <Button
                        color="danger"
                        onClick={handleConfirm}
                        disabled={deleting}
                        className="rounded-pill px-4 d-flex align-items-center gap-2"
                    >
                        {deleting ? (
                            <>
                                <Spinner size="sm" />
                                Siliniyor...
                            </>
                        ) : (
                            <>
                                <i className="bx bx-trash"></i>
                                Evet, Sil
                            </>
                        )}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default ScheduledReportDeleteModal;